import { createSlice } from "@reduxjs/toolkit";
import { startListening } from "../listenerMiddleware";
import { postAuthentication, postRefreshToken, postLogout } from "../../services/autenticacao";
import { DefaultError, RefreshTokenError } from "../../constants/messages";
import { setRefreshToken, clearAuthStorage, getRefreshToken } from "../../helpers/authStorage";
import { profileLoaded } from "../profile/profileSlice";

const initialState = {
    user: null,
    loading: false,
    errors: [],
    initialized: false,
    isLoggingOut: false,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginUser(state) {
            state.loading = true,
            state.errors = []
        },
        loginUserRefresh(state) {
            state.loading = true,
            state.errors = []
        },
        loginUserSuccess(state,action) {
            state.user = action.payload.user,
            state.loading = false,
            state.errors = [],
            state.isLoggingOut = false
        },
        loginUserError(state,action) {
            state.user = null,
            state.loading = false,
            state.errors = action.payload ?? [],
            state.isLoggingOut = false
        },
        logoutUser(state,action) {
            const {errors} = action.payload;
            state.user = null,
            state.loading = false,
            state.errors = errors ?? [],
            state.isLoggingOut = true
        },
        setInitialized(state) {
            state.initialized = true;
        }
    }
});

export const {
    loginUser,
    loginUserRefresh,
    loginUserSuccess,
    loginUserError,
    logoutUser,
    setInitialized
} = authSlice.actions;

export default authSlice.reducer;

startListening({
    actionCreator: loginUser,
    effect: async (action, listenerApi) => {
        const { navigate, user: userPayload, location } = action.payload;
        const { usuario, senha } = userPayload;
        const loginUser = await loginWithUserPasswordAsync(usuario, senha);
        const { messages = [], user = undefined } = loginUser;
        if (messages.length > 0)
            listenerApi.dispatch(loginUserError(messages[0]));
        else if (user === undefined){
            listenerApi.dispatch(loginUserError([DefaultError]));
        }
        else{
            listenerApi.dispatch(loginUserSuccess({user,navigate, location}));
        }
    },
})

startListening({
    actionCreator: loginUserRefresh,
    effect: async (action, listenerApi) => {
        const { navigate, location } = action.payload ?? {};
        const state = listenerApi.getState();
        if (state.auth?.isLoggingOut) {
            listenerApi.dispatch(loginUserError([]));
            listenerApi.dispatch(setInitialized());
            return;
        }
        try {
            const loginUser = await refreshTokenValidationAsync();
            if (!loginUser.errors)
                listenerApi.dispatch(loginUserSuccess({user:loginUser.user, navigate, location}));
            else
                listenerApi.dispatch(loginUserError([RefreshTokenError]));
        } catch {
            listenerApi.dispatch(loginUserError([RefreshTokenError]));
        } finally {
            listenerApi.dispatch(setInitialized());
        }
    },
})

startListening({
    actionCreator: loginUserSuccess,
    effect: async (action, listenerApi) => {
        const { user, navigate, location } = action.payload;
        try {
            await loginSuccessAsync(user, navigate, location);
            listenerApi.dispatch(profileLoaded());
        }
        catch {
            listenerApi.dispatch(loginUserError([DefaultError]));
        }
    },
})

startListening({
    actionCreator: logoutUser,
    effect: async (action, listenerApi) => {
        const { refreshToken, navigate, history } = action.payload ?? {};
        await logoutAsync(refreshToken, navigate, history);
    },
})

const loginWithUserPasswordAsync = async (cpf, password) =>
    postAuthentication(cpf, password)
        .then((user) => user)
        .catch((error) => error);

const refreshTokenValidationAsync = async () =>
    await postRefreshToken()
        .then((authUser) => authUser)
        .catch((error) => error);

const loginSuccessAsync = async (user, navigate, location) => {
    setRefreshToken(user?.refreshToken ?? null);
    if (navigate) {
        const redirectTo = location?.state?.from?.pathname || '/';
        navigate(redirectTo);
    }
}

const logoutAsync = async (refreshToken, navigate, history) => {
    const tokenToUse = refreshToken || getRefreshToken();
    await postLogout(tokenToUse);
    clearAuthStorage();
    if (navigate) {
        navigate('/user/login');
    } else if (history) {
        history.push('/user/login');
    }
};
